'use client'
import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { RxCrossCircled, RxDownload } from 'react-icons/rx'
import { MdOutlineFileDownloadDone } from 'react-icons/md'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { toast } from 'react-toastify'

interface ZoomModalProps {
  templateImage: string
  hiresImageUrl?: string | undefined
  setShowZoomModal: (val: boolean) => void
}

const ZoomModal: React.FC<ZoomModalProps> = (props) => {
  const { templateImage, setShowZoomModal, hiresImageUrl } = props
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloaded, setIsDownloaded] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowZoomModal(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [setShowZoomModal])

  const handleDownload = async () => {
    if (!hiresImageUrl || isLoading) return

    try {
      setIsLoading(true)
      setIsDownloaded(false)

      const response = await fetch(hiresImageUrl)
      if (!response.ok) throw new Error('Failed to fetch image')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = hiresImageUrl.split('/').pop() || 'download.jpg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      setIsDownloaded(true)
      setTimeout(() => {
        setIsDownloaded(false)
      }, 2000) // reset back to normal after 2s
    } catch (error) {
        console.error('Download failed:', error)
        toast.error(
            "Unable to download image. Please try again.",
            { autoClose: 4000 }
        );
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative">
        <div className="absolute top-2 right-2 flex text-white text-2xl p-1 gap-3">
          {hiresImageUrl && (
            <button
              onClick={handleDownload}
              disabled={isLoading}
              className={`transition-transform cursor-pointer ${
                isLoading ? 'opacity-60 cursor-not-allowed' : 'hover:scale-110'
              }`}
              aria-label="Download image"
            >
              {isLoading ? (
                <AiOutlineLoading3Quarters className="animate-spin" size={28} />
              ) : isDownloaded ? (
                <MdOutlineFileDownloadDone size={28} />
              ) : (
                <RxDownload size={28} />
              )}
            </button>
          )}
          <button
            onClick={() => setShowZoomModal(false)}
            className="hover:scale-110 transition-transform cursor-pointer"
            aria-label="Close modal"
          >
            <RxCrossCircled size={28} />
          </button>
        </div>

        <Image
          src={hiresImageUrl || templateImage}
          alt="Zoomed image preview"
          width={1000}
          height={1000}
          className="rounded max-w-[100vw] max-h-[95vh] object-contain"
        />
      </div>
    </div>
  )
}

export default ZoomModal
